import React, { useMemo, useState } from 'react';
import { SongAdProject } from '../../services/api/songAdService';
import { TaskyButton, TaskyTextarea, TaskyBadge, TaskyCard, TaskyAlert, TaskyInput, TaskyConfirmDialog } from '../tasky';
import { Sparkles, Save, CheckCircle2, RefreshCw, Wand2, SlidersHorizontal } from 'lucide-react';
import { CM_TYPES, MOOD_TAGS, TEMPO_OPTIONS, VOCAL_OPTIONS } from './SongAdSettingsForm';

/**
 * ステップ①: 歌詞。
 *
 * 表示歌詞（漢字混じり）とかな歌詞（歌唱にそのまま使われる）を並べて両方編集できる（v2から維持）。
 * v3で追加: 設定サマリー＋「設定を変更」導線・セクション表示（イントロ/Aメロ/サビ/アウトロ）・
 * AI修正チップ（定型＋自由入力→instruction付き再生成）・後工程生成済み時の確認モーダル（§8.5）。
 *
 * - 「読みを自動で振り直す」: 表示歌詞のみ送信し、サーバー側でかな歌詞を再生成する
 * - 「保存して次へ」: 表示歌詞＋かな歌詞の両方を送信し、そのまま保存する（サーバー再生成なし）。
 *   保存に成功すると呼び出し元（SongAdEditorPage）が②楽曲ステップへ自動遷移する
 * - 「作り直す」/AI修正チップ: 歌詞そのものをAIで作り直す（instructionを渡せる）
 */

interface LyricsStepProps {
  project: SongAdProject;
  lyricsDraft: string;
  kanaDraft: string;
  onLyricsDraftChange: (value: string) => void;
  onKanaDraftChange: (value: string) => void;
  isGenerating: boolean;
  isSaving: boolean;
  isRegeneratingKana: boolean;
  onGenerate: (instruction?: string) => Promise<void>;
  onSaveFixed: () => Promise<void>;
  onRegenerateKana: () => Promise<void>;
  /** song_url または scenes が既に存在する（後工程生成済み）。§8.5の確認モーダル要否に使う */
  hasDownstream?: boolean;
  /** 「設定を変更」導線。SongAdEditorPageの設定モーダルを開く */
  onEditSettings?: () => void;
}

const countLines = (text: string): number => (text.trim() ? text.split('\n').length : 0);

const SECTION_LABELS: Record<string, string> = {
  intro: 'イントロ',
  verse: 'Aメロ',
  chorus: 'サビ',
  outro: 'アウトロ',
};

const AI_CHIPS = ['もっとキャッチーに', '商品名を増やす', 'やわらかく'];

const LyricsStep: React.FC<LyricsStepProps> = ({
  project,
  lyricsDraft,
  kanaDraft,
  onLyricsDraftChange,
  onKanaDraftChange,
  isGenerating,
  isSaving,
  isRegeneratingKana,
  onGenerate,
  onSaveFixed,
  onRegenerateKana,
  hasDownstream = false,
  onEditSettings,
}) => {
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);
  const [aiInstruction, setAiInstruction] = useState('');
  const [pendingAction, setPendingAction] = useState<null | { type: 'save' | 'ai'; instruction?: string }>(null);
  const hasLyrics = !!project.lyrics;

  const lyricsLineCount = useMemo(() => countLines(lyricsDraft), [lyricsDraft]);
  const kanaLineCount = useMemo(() => countLines(kanaDraft), [kanaDraft]);
  const lineMismatch = lyricsLineCount !== kanaLineCount;

  const cmTypeLabel = CM_TYPES.find((c) => c.key === project.cm_type)?.label ?? project.cm_type;
  const moodLabel = MOOD_TAGS.find((m) => m.key === project.style_tags[0])?.label ?? project.style_tags[0];
  const vocalLabel = VOCAL_OPTIONS.find((v) => v.key === project.vocal_gender)?.label ?? project.vocal_gender;
  const tempoLabel = TEMPO_OPTIONS.find((t) => t.key === project.tempo)?.label ?? project.tempo;
  const summaryChips = [
    cmTypeLabel,
    moodLabel,
    vocalLabel,
    tempoLabel,
    `${project.target_seconds}秒`,
    project.intro ? 'イントロあり' : 'すぐ歌い出し',
  ].filter((t): t is string => !!t);

  const sectionRanges = useMemo(() => {
    if (!project.lyrics_sections) return [];
    return project.lyrics_sections
      .filter((s) => s.line_indexes.length > 0)
      .map((s) => {
        const min = Math.min(...s.line_indexes) + 1;
        const max = Math.max(...s.line_indexes) + 1;
        return {
          label: SECTION_LABELS[s.label] ?? s.label,
          range: min === max ? `${min}行目` : `${min}〜${max}行目`,
        };
      });
  }, [project.lyrics_sections]);

  const handleGenerateClick = () => {
    if (hasLyrics && !confirmRegenerate) {
      setConfirmRegenerate(true);
      return;
    }
    setConfirmRegenerate(false);
    onGenerate();
  };

  const runGuarded = (action: () => void, type: 'save' | 'ai', instruction?: string) => {
    if (hasDownstream) {
      setPendingAction({ type, instruction });
      return;
    }
    action();
  };

  const handleSaveClick = () => runGuarded(() => onSaveFixed(), 'save');

  const applyAiChip = (instruction: string) => {
    runGuarded(() => onGenerate(instruction), 'ai', instruction);
  };

  const handleAiFreeSubmit = () => {
    const instruction = aiInstruction.trim();
    if (!instruction) return;
    runGuarded(() => onGenerate(instruction), 'ai', instruction);
  };

  const handleConfirmedPendingAction = () => {
    if (!pendingAction) return;
    if (pendingAction.type === 'save') onSaveFixed();
    else onGenerate(pendingAction.instruction);
    setPendingAction(null);
  };

  return (
    <div className="space-y-5">
      {/* 設定サマリー */}
      <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-gray-500">
        {summaryChips.map((t, i) => (
          <span key={`${i}-${t}`} className="bg-warm-surface rounded-full px-2.5 py-1">
            {t}
          </span>
        ))}
        {onEditSettings && (
          <button
            type="button"
            onClick={onEditSettings}
            className="flex items-center gap-1 text-primary hover:underline ml-1"
          >
            <SlidersHorizontal className="w-3 h-3" />
            設定を変更
          </button>
        )}
      </div>

      <TaskyCard variant="warm" padding="md">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-dark">歌詞</h3>
          {project.lyrics_fixed_at && (
            <TaskyBadge variant="success">
              <CheckCircle2 className="w-3 h-3" />
              フィックス済み
            </TaskyBadge>
          )}
        </div>

        {!hasLyrics ? (
          <div className="text-center py-10 text-gray-400 text-sm border border-dashed border-warm-border rounded-tasky-md">
            <p className="mb-3">商品情報・曲調・テンポからAIが歌詞を生成します（表示用の漢字歌詞＋歌唱用のかな歌詞）</p>
            <TaskyButton onClick={handleGenerateClick} isLoading={isGenerating}>
              <Sparkles className="w-4 h-4" />
              歌詞を生成
            </TaskyButton>
          </div>
        ) : (
          <>
            {sectionRanges.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {sectionRanges.map((s, i) => (
                  <span
                    key={`${s.label}-${i}`}
                    className="inline-flex items-center gap-1 text-[10px] font-semibold tracking-wide text-primary bg-primary/10 rounded-full px-2 py-0.5"
                  >
                    {s.label}
                    <span className="font-normal text-gray-400">{s.range}</span>
                  </span>
                ))}
              </div>
            )}

            <p className="text-xs text-gray-500 mb-3">
              かな歌詞がそのまま歌唱に使われます。商品名・固有名詞の読みが間違っている場合はここで修正してください。
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-medium text-gray-600">表示歌詞</label>
                  <span className="text-[11px] text-gray-400">表示{lyricsLineCount}行</span>
                </div>
                <TaskyTextarea
                  value={lyricsDraft}
                  onChange={(e) => onLyricsDraftChange(e.target.value)}
                  rows={12}
                  maxLength={3000}
                  disabled={isGenerating}
                  className="font-mono text-sm"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-medium text-gray-600">かな歌詞（歌唱に使用）</label>
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-[11px] ${
                        lineMismatch ? 'text-destructive font-semibold' : 'text-gray-400'
                      }`}
                    >
                      かな{kanaLineCount}行
                    </span>
                    <button
                      type="button"
                      onClick={() => onRegenerateKana()}
                      disabled={!lyricsDraft.trim() || isGenerating || isRegeneratingKana}
                      className="flex items-center gap-1 text-[11px] text-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:no-underline"
                    >
                      <RefreshCw className={`w-3 h-3 ${isRegeneratingKana ? 'animate-spin' : ''}`} />
                      読みを自動で振り直す
                    </button>
                  </div>
                </div>
                <TaskyTextarea
                  value={kanaDraft}
                  onChange={(e) => onKanaDraftChange(e.target.value)}
                  rows={12}
                  maxLength={3000}
                  disabled={isGenerating}
                  hasError={lineMismatch}
                  className="font-mono text-sm"
                />
              </div>
            </div>

            {lineMismatch && (
              <TaskyAlert tone="error" className="mt-3">
                表示歌詞（{lyricsLineCount}行）とかな歌詞（{kanaLineCount}行）の行数が一致していないため保存できません。行数を揃えるか、「読みを自動で振り直す」でかな歌詞を作り直してください。
              </TaskyAlert>
            )}

            {/* AI修正チップ */}
            <div className="mt-4 pt-4 border-t border-warm-border">
              <p className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1">
                <Wand2 className="w-3.5 h-3.5 text-primary" />
                AIで修正する
              </p>
              <div className="flex flex-wrap gap-2 mb-2">
                {AI_CHIPS.map((label) => (
                  <button
                    key={label}
                    type="button"
                    disabled={isGenerating}
                    onClick={() => applyAiChip(label)}
                    className="flex items-center gap-1 rounded-full pl-2.5 pr-3.5 py-1.5 text-xs text-gray-600 border border-warm-border hover:border-primary transition-colors disabled:opacity-50"
                  >
                    <Sparkles className="w-3.5 h-3.5 text-primary" />
                    {label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <TaskyInput
                  value={aiInstruction}
                  onChange={(e) => setAiInstruction(e.target.value)}
                  // v8: Enterキーで即送信されると、変換確定やタイプミスの途中でも
                  // AI修正（歌詞の作り直し）が走ってしまうため、Enterでは何もしない
                  // （送信は「適用」ボタンのみ。入力欄は元々1行のTaskyInputで、
                  // ここに<form>もないためEnterのデフォルト動作も特になくノーオペで良い）
                  placeholder="例: サビをもっと商品名で埋める"
                  disabled={isGenerating}
                  className="flex-1"
                />
                <TaskyButton
                  variant="outline"
                  size="sm"
                  onClick={handleAiFreeSubmit}
                  disabled={!aiInstruction.trim() || isGenerating}
                  isLoading={isGenerating}
                >
                  適用
                </TaskyButton>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-4">
              <TaskyButton
                onClick={handleSaveClick}
                isLoading={isSaving}
                disabled={!lyricsDraft.trim() || !kanaDraft.trim() || lineMismatch || isGenerating}
              >
                <Save className="w-4 h-4" />
                保存して次へ
              </TaskyButton>
              <TaskyButton variant="ghost" onClick={handleGenerateClick} isLoading={isGenerating}>
                <Sparkles className="w-4 h-4" />
                作り直す
              </TaskyButton>
            </div>

            {confirmRegenerate && (
              <TaskyAlert tone="warning" className="mt-3">
                現在の歌詞（編集中の内容含む）は破棄され、AIが新しく生成し直します。よろしいですか？
                <div className="flex gap-2 mt-2">
                  <TaskyButton size="sm" variant="destructive" onClick={handleGenerateClick}>
                    破棄して作り直す
                  </TaskyButton>
                  <TaskyButton size="sm" variant="outline" onClick={() => setConfirmRegenerate(false)}>
                    キャンセル
                  </TaskyButton>
                </div>
              </TaskyAlert>
            )}
          </>
        )}
      </TaskyCard>

      <TaskyConfirmDialog
        open={!!pendingAction}
        onClose={() => setPendingAction(null)}
        onConfirm={handleConfirmedPendingAction}
        tone="warning"
        title="歌詞を変更しますか？"
        description="歌詞を変更すると、生成済みの楽曲・シーン・動画は作り直しが必要になります。よろしいですか？"
        confirmText="変更する"
      />
    </div>
  );
};

export default LyricsStep;
